import { call, put, takeLatest } from 'redux-saga/effects';
import * as types from '../constants/actionTypes';
import { rbacAPI } from '../../api/config';
import { message } from 'antd';

// Role Sagas
function* fetchRbacRolesSaga(action) {
  try {
    const response = yield call(rbacAPI.fetchRoles, action.payload);
    yield put({
      type: types.FETCH_RBAC_ROLES_SUCCESS,
      payload: response.data.roles
    });
  } catch (error) {
    yield put({
      type: types.FETCH_RBAC_ROLES_FAILURE,
      payload: error.response?.data?.error || 'Failed to fetch roles'
    });
    message.error(error.response?.data?.error || 'Failed to fetch roles');
  }
}

function* getRbacRoleSaga(action) {
  try {
    const response = yield call(rbacAPI.getRole, action.payload);
    yield put({
      type: types.GET_RBAC_ROLE_SUCCESS,
      payload: response.data.role
    });
  } catch (error) {
    yield put({
      type: types.GET_RBAC_ROLE_FAILURE,
      payload: error.response?.data?.error || 'Failed to fetch role'
    });
    message.error(error.response?.data?.error || 'Failed to fetch role');
  }
}

function* createRbacRoleSaga(action) {
  try {
    const response = yield call(rbacAPI.createRole, action.payload);
    yield put({
      type: types.CREATE_RBAC_ROLE_SUCCESS,
      payload: response.data.role
    });
    message.success('Role created successfully');
    yield put({ type: types.FETCH_RBAC_ROLES_REQUEST });
  } catch (error) {
    yield put({
      type: types.CREATE_RBAC_ROLE_FAILURE,
      payload: error.response?.data?.error || 'Failed to create role'
    });
    message.error(error.response?.data?.error || 'Failed to create role');
  }
}

function* updateRbacRoleSaga(action) {
  try {
    const { id, roleData } = action.payload;
    const response = yield call(rbacAPI.updateRole, id, roleData);
    yield put({
      type: types.UPDATE_RBAC_ROLE_SUCCESS,
      payload: response.data.role
    });
    message.success('Role updated successfully');
    yield put({ type: types.FETCH_RBAC_ROLES_REQUEST });
  } catch (error) {
    yield put({
      type: types.UPDATE_RBAC_ROLE_FAILURE,
      payload: error.response?.data?.error || 'Failed to update role'
    });
    message.error(error.response?.data?.error || 'Failed to update role');
  }
}

function* deleteRbacRoleSaga(action) {
  try {
    yield call(rbacAPI.deleteRole, action.payload);
    yield put({
      type: types.DELETE_RBAC_ROLE_SUCCESS,
      payload: action.payload
    });
    message.success('Role deleted successfully');
    yield put({ type: types.FETCH_RBAC_ROLES_REQUEST });
  } catch (error) {
    yield put({
      type: types.DELETE_RBAC_ROLE_FAILURE,
      payload: error.response?.data?.error || 'Failed to delete role'
    });
    message.error(error.response?.data?.error || 'Failed to delete role');
  }
}

function* fetchRoleUsersSaga(action) {
  try {
    const response = yield call(rbacAPI.getRoleUsers, action.payload);
    yield put({
      type: types.FETCH_ROLE_USERS_SUCCESS,
      payload: response.data.users
    });
  } catch (error) {
    yield put({
      type: types.FETCH_ROLE_USERS_FAILURE,
      payload: error.response?.data?.error || 'Failed to fetch role users'
    });
    message.error(error.response?.data?.error || 'Failed to fetch role users');
  }
}

// Action Sagas
function* fetchRbacActionsSaga(action) {
  try {
    const response = yield call(rbacAPI.fetchActions, action.payload);
    yield put({
      type: types.FETCH_RBAC_ACTIONS_SUCCESS,
      payload: response.data.actions
    });
  } catch (error) {
    yield put({
      type: types.FETCH_RBAC_ACTIONS_FAILURE,
      payload: error.response?.data?.error || 'Failed to fetch actions'
    });
    message.error(error.response?.data?.error || 'Failed to fetch actions');
  }
}

function* fetchActionsGroupedSaga() {
  try {
    const response = yield call(rbacAPI.getActionsGrouped);
    yield put({
      type: types.FETCH_ACTIONS_GROUPED_SUCCESS,
      payload: response.data.data
    });
  } catch (error) {
    yield put({
      type: types.FETCH_ACTIONS_GROUPED_FAILURE,
      payload: error.response?.data?.error || 'Failed to fetch grouped actions'
    });
    message.error(error.response?.data?.error || 'Failed to fetch grouped actions');
  }
}

function* createRbacActionSaga(action) {
  try {
    const response = yield call(rbacAPI.createAction, action.payload);
    yield put({
      type: types.CREATE_RBAC_ACTION_SUCCESS,
      payload: response.data.action
    });
    message.success('Action created successfully');
    yield put({ type: types.FETCH_RBAC_ACTIONS_REQUEST });
    yield put({ type: types.FETCH_ACTIONS_GROUPED_REQUEST });
  } catch (error) {
    yield put({
      type: types.CREATE_RBAC_ACTION_FAILURE,
      payload: error.response?.data?.error || 'Failed to create action'
    });
    message.error(error.response?.data?.error || 'Failed to create action');
  }
}

function* updateRbacActionSaga(action) {
  try {
    const { id, actionData } = action.payload;
    const response = yield call(rbacAPI.updateAction, id, actionData);
    yield put({
      type: types.UPDATE_RBAC_ACTION_SUCCESS,
      payload: response.data.action
    });
    message.success('Action updated successfully');
    yield put({ type: types.FETCH_RBAC_ACTIONS_REQUEST });
    yield put({ type: types.FETCH_ACTIONS_GROUPED_REQUEST });
  } catch (error) {
    yield put({
      type: types.UPDATE_RBAC_ACTION_FAILURE,
      payload: error.response?.data?.error || 'Failed to update action'
    });
    message.error(error.response?.data?.error || 'Failed to update action');
  }
}

function* deleteRbacActionSaga(action) {
  try {
    yield call(rbacAPI.deleteAction, action.payload);
    yield put({
      type: types.DELETE_RBAC_ACTION_SUCCESS,
      payload: action.payload
    });
    message.success('Action deleted successfully');
    yield put({ type: types.FETCH_RBAC_ACTIONS_REQUEST });
    yield put({ type: types.FETCH_ACTIONS_GROUPED_REQUEST });
  } catch (error) {
    yield put({
      type: types.DELETE_RBAC_ACTION_FAILURE,
      payload: error.response?.data?.error || 'Failed to delete action'
    });
    message.error(error.response?.data?.error || 'Failed to delete action');
  }
}

// Permission Sagas
function* fetchRolePermissionsSaga(action) {
  try {
    const response = yield call(rbacAPI.getRolePermissions, action.payload);
    yield put({
      type: types.FETCH_ROLE_PERMISSIONS_SUCCESS,
      payload: response.data.permissions
    });
  } catch (error) {
    yield put({
      type: types.FETCH_ROLE_PERMISSIONS_FAILURE,
      payload: error.response?.data?.error || 'Failed to fetch permissions'
    });
    message.error(error.response?.data?.error || 'Failed to fetch permissions');
  }
}

function* bulkAssignPermissionsSaga(action) {
  try {
    yield call(rbacAPI.bulkAssignPermissions, action.payload);
    yield put({
      type: types.BULK_ASSIGN_PERMISSIONS_SUCCESS
    });
    message.success('Permissions assigned successfully');
    // Refresh permissions for the role
    yield put({
      type: types.FETCH_ROLE_PERMISSIONS_REQUEST,
      payload: action.payload.role_id
    });
  } catch (error) {
    yield put({
      type: types.BULK_ASSIGN_PERMISSIONS_FAILURE,
      payload: error.response?.data?.error || 'Failed to assign permissions'
    });
    message.error(error.response?.data?.error || 'Failed to assign permissions');
  }
}

function* togglePermissionSaga(action) {
  try {
    yield call(rbacAPI.togglePermission, action.payload);
    yield put({
      type: types.TOGGLE_PERMISSION_SUCCESS
    });
    message.success('Permission toggled successfully');
    // Refresh permissions for the role
    yield put({
      type: types.FETCH_ROLE_PERMISSIONS_REQUEST,
      payload: action.payload.role_id
    });
  } catch (error) {
    yield put({
      type: types.TOGGLE_PERMISSION_FAILURE,
      payload: error.response?.data?.error || 'Failed to toggle permission'
    });
    message.error(error.response?.data?.error || 'Failed to toggle permission');
  }
}

// User Sagas
function* fetchRbacUsersSaga() {
  try {
    const response = yield call(rbacAPI.fetchUsers);
    yield put({
      type: types.FETCH_RBAC_USERS_SUCCESS,
      payload: response.data.users
    });
  } catch (error) {
    yield put({
      type: types.FETCH_RBAC_USERS_FAILURE,
      payload: error.response?.data?.error || 'Failed to fetch users'
    });
    message.error(error.response?.data?.error || 'Failed to fetch users');
  }
}

function* getRbacUserSaga(action) {
  try {
    const response = yield call(rbacAPI.getUser, action.payload);
    yield put({
      type: types.GET_RBAC_USER_SUCCESS,
      payload: response.data.user
    });
  } catch (error) {
    yield put({
      type: types.GET_RBAC_USER_FAILURE,
      payload: error.response?.data?.error || 'Failed to fetch user'
    });
    message.error(error.response?.data?.error || 'Failed to fetch user');
  }
}

function* updateUserRoleSaga(action) {
  try {
    const { userId, roleId } = action.payload;
    yield call(rbacAPI.updateUserRole, userId, roleId);
    yield put({
      type: types.UPDATE_USER_ROLE_SUCCESS,
      payload: { id: userId, role_id: roleId }
    });
    message.success('User role updated successfully');
    yield put({ type: types.FETCH_RBAC_USERS_REQUEST });
  } catch (error) {
    yield put({
      type: types.UPDATE_USER_ROLE_FAILURE,
      payload: error.response?.data?.error || 'Failed to update user role'
    });
    message.error(error.response?.data?.error || 'Failed to update user role');
  }
}

function* removeUserRoleSaga(action) {
  try {
    yield call(rbacAPI.removeUserRole, action.payload);
    yield put({
      type: types.REMOVE_USER_ROLE_SUCCESS,
      payload: action.payload
    });
    message.success('User role removed successfully');
    yield put({ type: types.FETCH_RBAC_USERS_REQUEST });
  } catch (error) {
    yield put({
      type: types.REMOVE_USER_ROLE_FAILURE,
      payload: error.response?.data?.error || 'Failed to remove user role'
    });
    message.error(error.response?.data?.error || 'Failed to remove user role');
  }
}

// Watcher Saga
export default function* rbacSaga() {
  yield takeLatest(types.FETCH_RBAC_ROLES_REQUEST, fetchRbacRolesSaga);
  yield takeLatest(types.GET_RBAC_ROLE_REQUEST, getRbacRoleSaga);
  yield takeLatest(types.CREATE_RBAC_ROLE_REQUEST, createRbacRoleSaga);
  yield takeLatest(types.UPDATE_RBAC_ROLE_REQUEST, updateRbacRoleSaga);
  yield takeLatest(types.DELETE_RBAC_ROLE_REQUEST, deleteRbacRoleSaga);
  yield takeLatest(types.FETCH_ROLE_USERS_REQUEST, fetchRoleUsersSaga);
  
  yield takeLatest(types.FETCH_RBAC_ACTIONS_REQUEST, fetchRbacActionsSaga);
  yield takeLatest(types.FETCH_ACTIONS_GROUPED_REQUEST, fetchActionsGroupedSaga);
  yield takeLatest(types.CREATE_RBAC_ACTION_REQUEST, createRbacActionSaga);
  yield takeLatest(types.UPDATE_RBAC_ACTION_REQUEST, updateRbacActionSaga);
  yield takeLatest(types.DELETE_RBAC_ACTION_REQUEST, deleteRbacActionSaga);
  
  yield takeLatest(types.FETCH_ROLE_PERMISSIONS_REQUEST, fetchRolePermissionsSaga);
  yield takeLatest(types.BULK_ASSIGN_PERMISSIONS_REQUEST, bulkAssignPermissionsSaga);
  yield takeLatest(types.TOGGLE_PERMISSION_REQUEST, togglePermissionSaga);
  
  yield takeLatest(types.FETCH_RBAC_USERS_REQUEST, fetchRbacUsersSaga);
  yield takeLatest(types.GET_RBAC_USER_REQUEST, getRbacUserSaga);
  yield takeLatest(types.UPDATE_USER_ROLE_REQUEST, updateUserRoleSaga);
  yield takeLatest(types.REMOVE_USER_ROLE_REQUEST, removeUserRoleSaga);
}
